import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { partyApi } from '../../services/api.party';
import config from '../../config/environment';

const PartyModal = ({ party, onClose, onSaved }) => {
  const { t } = useTranslation();
  const isEdit = !!party;
  const [form, setForm] = useState({
    name: '',
    shortName: '',
    symbolUrl: ''
  });
  const [preview, setPreview] = useState('');
  const [symbolFile, setSymbolFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const fileInputRef = useRef(null);

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('blob:') || url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    const baseUrl = config.API_URL.replace(/\/api$/, '');
    return `${baseUrl}${url}`;
  };

  useEffect(() => {
    if (party) {
      setForm({
        name: party.name || '',
        shortName: party.shortName || '',
        symbolUrl: party.symbolUrl || ''
      });
      setPreview(party.symbolUrl ? getFullImageUrl(party.symbolUrl) : '');
    }
  }, [party]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMessage(null);
    try {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(f.type)) {
        throw new Error('Only PNG, JPG, JPEG, GIF, SVG files are allowed');
      }

      if (f.size > 2 * 1024 * 1024) {
        throw new Error('File size must be less than 2MB');
      }

      const urlObj = URL.createObjectURL(f);
      setPreview(urlObj);
      setSymbolFile(f);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Invalid file' });
    }
  };

  const uploadIfAny = async () => {
    if (!symbolFile) return form.symbolUrl || '';
    const result = await partyApi.uploadSymbol(symbolFile);
    return result.url;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      if (!form.name?.trim()) throw new Error('Name is required');

      const symbolUrl = await uploadIfAny();
      const payload = { ...form, symbolUrl };

      if (isEdit) {
        await partyApi.update(party.id, payload);
      } else {
        await partyApi.create(payload);
      }
      onSaved();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to save party' });
    } finally {
      setSaving(false);
    }
  };

  const clearSymbol = () => {
    setSymbolFile(null);
    setPreview('');
    setForm(s => ({ ...s, symbolUrl: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content party-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            <i className="fas fa-flag"></i> {isEdit ? t('party.editParty') : t('party.newParty')}
          </h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {message && (
          <div className={`alert ${message.type}`}>
            <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
            {message.text}
          </div>
        )}

        <form onSubmit={submit} className="modal-body">
          <div className="form-group">
            <label htmlFor="name">{t('party.nameRequired')}</label>
            <input
              id="name"
              name="name"
              type="text"
              value={form.name}
              onChange={onChange}
              placeholder={t('party.namePlaceholder')}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="shortName">{t('party.shortNameLabel')}</label>
            <input
              id="shortName"
              name="shortName"
              type="text"
              value={form.shortName}
              onChange={onChange}
              placeholder={t('party.shortNamePlaceholder')}
              maxLength={20}
            />
          </div>

          <div className="form-group">
            <label>{t('party.symbolLabel')}</label>
            <div className="symbol-picker">
              <div className="symbol-preview">
                {preview ? (
                  <img 
                    src={preview} 
                    alt="Symbol preview" 
                    onError={(e) => {
                      console.error('Preview image load error:', preview);
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }} 
                  />
                ) : null}
                <div className="symbol-placeholder" style={{ display: preview ? 'none' : 'flex' }}>
                  <i className="fas fa-image"></i>
                </div>
              </div>
              <div className="symbol-actions">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/svg+xml"
                  style={{ display: 'none' }}
                  onChange={onFile}
                />
                <button type="button" className="btn" onClick={onPickFile}>
                  <i className="fas fa-upload"></i> {t('party.uploadSymbol')}
                </button>
                <button
                  type="button"
                  className="btn light"
                  onClick={clearSymbol}
                  disabled={!preview && !form.symbolUrl}
                >
                  <i className="fas fa-times"></i> {t('party.clearSymbol')}
                </button>
              </div>
            </div>
            <small className="field-hint">{t('party.symbolHint')}</small>
          </div>

          <div className="modal-footer inline-footer">
            <button type="button" className="btn" onClick={onClose} disabled={saving}>
              {t('party.cancel')}
            </button>
            <button type="submit" className="btn primary" disabled={saving}>
              {saving ? (
                <>
                  <div className="spinner-small"></div>
                  {t('party.saving')}
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i> {t('party.save')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartyModal;
